/* eslint-env browser */
import { useEffect } from 'react';

/**
 * Custom BootAnim for Precina Patients app
 * Uses the Precina medical logo with healthcare-themed animations
 */

export const stylesheet = `
	@keyframes precina-logo-pulse {
		0%,
		100% {
			opacity: 0.95;
			transform: scale(1);
		}
		50% {
			opacity: 1;
			transform: scale(1.03);
		}
	}

	@keyframes precina-rings-pulse {
		0%,
		100% {
			opacity: 0.8;
			transform: scale(1);
		}
		50% {
			opacity: 1;
			transform: scale(1.05);
		}
	}

	@keyframes precina-cross-glow {
		0%,
		100% {
			filter: drop-shadow(0 0 6px rgba(0, 132, 213, 0.4));
			transform: scale(1);
		}
		50% {
			filter: drop-shadow(0 0 12px rgba(0, 132, 213, 0.7));
			transform: scale(1.05);
		}
	}

	@keyframes precina-center-pulse {
		0%,
		100% {
			transform: scale(1);
			opacity: 0.9;
		}
		50% {
			transform: scale(1.08);
			opacity: 1;
		}
	}

	@keyframes precina-background-shimmer {
		0% {
			background-position: 0% 50%;
		}
		50% {
			background-position: 100% 50%;
		}
		100% {
			background-position: 0% 50%;
		}
	}

	.precina-boot-anim-wrapper {
		position: fixed;
		top: 0;
		left: 0;
		bottom: 0;
		right: 0;
		background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 20%, #93c5fd 100%);
		background-size: 400% 400%;
		animation: precina-background-shimmer 8s ease-in-out infinite;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.precina-logo-container {
		position: relative;
		width: 120px;
		height: 120px;
		animation: precina-logo-pulse 3s ease-in-out infinite;
	}

	@media (prefers-reduced-motion: reduce) {
		.precina-logo-container {
			animation: none;
		}
		.precina-rings {
			animation: none;
		}
		.precina-cross {
			animation: none;
		}
		.precina-center {
			animation: none;
		}
	}

	@media (min-width: 640px) {
		.precina-logo-container {
			width: 150px;
			height: 150px;
		}
	}

	@media (max-width: 640px) {
		.precina-logo-container {
			width: 96px;
			height: 96px;
		}
	}

	.precina-rings {
		animation: precina-rings-pulse 3s ease-in-out infinite;
		transform-origin: center;
	}

	.precina-cross {
		animation: precina-cross-glow 2s ease-in-out infinite;
		transform-origin: center;
	}

	.precina-center {
		animation: precina-center-pulse 4s ease-in-out infinite;
		transform-origin: center;
	}

	.precina-logo-svg {
		width: 100%;
		height: 100%;
		filter: drop-shadow(0 4px 20px rgba(0, 70, 128, 0.15));
	}
`;

export const BootAnimContent = () => (
	<div className="precina-boot-anim-wrapper">
		<div className="precina-logo-container">
			<svg
				className="precina-logo-svg"
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 135 135"
			>
				<g id="logoMark">
					{/* Center compound path with pulse animation - EXACT from original */}
					<path
						className="precina-center"
						id="logoMark_CompoundPathItem_"
						data-name="logoMark &lt;CompoundPathItem&gt;"
						d="M67.5,52.5a14.9927,14.9927,0,0,0-15,15v34.38a38.236,38.236,0,0,0,4.5,1.63V78.21A14.9994,14.9994,0,1,0,67.5,52.5Zm0,25.5A10.5,10.5,0,1,1,78,67.5,10.5115,10.5115,0,0,1,67.5,78Z"
						style={{ fill: '#004680' }}
					/>

					{/* Animated ring paths - EXACT from original */}
					<g className="precina-rings">
						<path
							id="logoMark_PathItem_"
							data-name="logoMark &lt;PathItem&gt;"
							d="M67.5,30A37.4946,37.4946,0,0,0,45.75,98.04V92.29A32.9768,32.9768,0,1,1,67.5,100.5a30.9347,30.9347,0,0,1-3.75-.22v4.53a35.8021,35.8021,0,0,0,3.75.19,37.5,37.5,0,0,0,0-75Z"
							style={{ fill: '#0084d5' }}
						/>
						<path
							id="logoMark_PathItem_2"
							data-name="logoMark &lt;PathItem&gt;"
							d="M67.5,41.25A26.26,26.26,0,0,0,45.75,82.21V67.5A21.75,21.75,0,1,1,67.5,89.25a21.09,21.09,0,0,1-3.75-.33v4.56a25.6616,25.6616,0,0,0,3.75.27,26.25,26.25,0,0,0,0-52.5Z"
							style={{ fill: '#0084d5' }}
						/>
					</g>

					{/* Medical cross with glow animation - EXACT from original */}
					<polygon
						className="precina-cross"
						id="logoMark_PathItem_3"
						data-name="logoMark &lt;PathItem&gt;"
						points="73.5 66 73.5 69 69 69 69 73.5 66 73.5 66 69 61.5 69 61.5 66 66 66 66 61.5 69 61.5 69 66 73.5 66"
						style={{ fill: '#0084d5' }}
					/>
				</g>
			</svg>
		</div>
	</div>
);

// Add styles to document
const style = document.createElement('style');
style.id = 'precina-boot-anim';
style.innerHTML = stylesheet;
const insertLocation = document.querySelector('script');
insertLocation.parentNode.insertBefore(style, insertLocation);

export function BootAnim() {
	useEffect(() => {
		return () => {
			const styleEl = document.getElementById('precina-boot-anim');
			if (styleEl && styleEl.parentNode) {
				try {
					styleEl.parentNode.removeChild(styleEl);
				} catch (error) {
					// eslint-disable-next-line no-console
					console.warn(`[BootAnim] Error removing style element:`, error);
				}
			}
		};
	}, []);

	return <BootAnimContent />;
}
